import React from 'react';
import {animated, useTransition} from 'react-spring';

import {useTheme} from '../styles';
import {TPlacementX, TPlacementY} from '../types';
import {easing} from '../utils';
import * as S from './style';
import {useFluidOverlayPlacement} from './usePlacement';

const useOverlayTransitions = (placementY: TPlacementY, isOpen: boolean, duration?: number) => {
  const {animation} = useTheme();

  let from = {};
  let enter = {};

  if (placementY === 'top') {
    from = {marginBottom: '-10px'};
    enter = {marginBottom: '0px'};
  } else {
    from = {marginTop: '-10px'};
    enter = {marginTop: '0px'};
  }

  return useTransition<boolean, React.CSSProperties>(isOpen, {
    from: {opacity: 0, ...from},
    enter: {opacity: 1, ...enter},
    leave: {opacity: 0},
    config: {
      duration: duration ?? animation.duration / 2,
      easing,
    },
  });
};

type TOverlayProps = {
  placementX?: TPlacementX;
  placementY?: TPlacementY;
  offsetY?: number;
  offsetX?: number;
  overlayColor?: string;
  hasOverlayArrow?: boolean;
  hasVerticalPaddings?: boolean;
  overlayComponent?: TAnyComponent;
};

type TContentProps = TOverlayProps &
  React.HTMLAttributes<HTMLElement> & {
    style?: TAnyObject;
    innerRef?: React.RefObject<HTMLElement>;
    headRef?: React.RefObject<HTMLDivElement>;
    children: React.ReactNode;
    onChangePlacementY?: (placementY: TPlacementY) => void;
  };

const SMALL_HEAD_WIDTH = 30;
const ARROW_OFFSET = 19.5;

const Content = React.memo<TContentProps>(
  ({
    overlayComponent = S.Overlay,
    placementY: propPlacementY,
    placementX: propPlacementX,
    className,
    style,
    children,
    innerRef,
    hasOverlayArrow,
    overlayColor,
    hasVerticalPaddings,
    offsetX: propOffsetX,
    offsetY,
    onChangePlacementY,
    headRef,
    ...overlayHandlers
  }) => {
    const ref = React.useRef<HTMLDivElement>(null);

    const {placementY, placementX} = useFluidOverlayPlacement({
      placementX: propPlacementX,
      placementY: propPlacementY,
      ref: innerRef ?? ref,
      headRef,
    });

    React.useLayoutEffect(() => onChangePlacementY?.(placementY), [placementY]);

    const Overlay = React.useMemo(() => animated(overlayComponent), [overlayComponent]);

    const offsetX = React.useMemo(() => {
      const headWidth = headRef?.current?.clientWidth;

      if (propOffsetX !== undefined || headWidth === undefined || !hasOverlayArrow) {
        return propOffsetX ?? 0;
      }

      const offset = placementX === 'center' ? 0 : headWidth / 2 - ARROW_OFFSET;

      return headWidth < SMALL_HEAD_WIDTH ? offset : 0;
    }, [headRef, hasOverlayArrow, propOffsetX, placementX]);

    return (
      <Overlay
        {...overlayHandlers}
        data-selector="EdnaOverlay:content"
        ref={innerRef ?? ref}
        hasArrow={hasOverlayArrow}
        backgroundColor={overlayColor}
        overlayColor={overlayColor}
        hasVerticalPaddings={hasVerticalPaddings}
        style={style}
        placementY={placementY}
        placementX={placementX}
        offsetY={offsetY}
        offsetX={offsetX}
        className={className}
      >
        {children}
      </Overlay>
    );
  },
);

type TFluidOverlayProps = TContentProps & {
  isOpen: boolean;
  delay?: number;
};

const FluidOverlay = React.memo<TFluidOverlayProps>(
  ({placementY: propPlacementY = 'bottom', delay, isOpen, children, ...overlayProps}) => {
    const isReplaceInProgressRef = React.useRef(false);
    const [placementY, setPlacementY] = React.useState(propPlacementY);
    const overlayTransitions = useOverlayTransitions(
      placementY,
      isOpen && !isReplaceInProgressRef.current,
      delay,
    );

    React.useLayoutEffect(() => {
      isReplaceInProgressRef.current = false;
    }, [placementY]);

    return (
      <>
        {overlayTransitions(
          (style, state) =>
            state && (
              <Content
                style={style}
                placementY={propPlacementY}
                onChangePlacementY={(nextPlacementY) => {
                  isReplaceInProgressRef.current = nextPlacementY !== placementY;
                  setPlacementY(nextPlacementY);
                }}
                {...overlayProps}
              >
                {children}
              </Content>
            ),
        )}
      </>
    );
  },
);

export type {TOverlayProps};
export {useOverlayTransitions, FluidOverlay};
